import { Q } from '@nozbe/watermelondb'
import { of, switchMap } from 'rxjs'
import {
  AccountDoc,
  DocType,
  EventDoc,
  GroupDoc,
  MasterLabelDoc,
  ParticipantDataDoc,
  ProfileDoc,
  UserSettingsDoc
} from 'types/api/changes'
import { CollectionType, Model } from 'types/model'
import { defaultGroupId } from 'utils/groups'

import Database from './Database'
import { ParticipantsAll, ParticipantsForGroup } from './Queries/GroupQueries'

class ObserveService {
  private EventsForGroupClause(groupId: string): Q.Clause {
    return Q.or(
      Q.where('GroupID', groupId),
      Q.where('SupplementaryGroupsIDs', Q.like(`%"${groupId}"%`)),
      Q.where('inheritedGroups', Q.like(`%"${groupId}"%`))
    )
  }

  public Groups(): Model<GroupDoc>[] {
    return Database.getCollection(CollectionType.GROUP)
      .query(Q.sortBy('_sortOrder', Q.desc))
      .observeWithColumns([
        '_sortOrder',
        'LastUpdate',
        'LastUpdateMessage',
        'PersonalGroupSyncStatus',
        'Name',
        'SyncProblem'
      ]) as unknown as Model<GroupDoc>[]
  }

  public TopGroups(): Model<GroupDoc>[] {
    return Database.getCollection(CollectionType.GROUP)
      .query(Q.sortBy('_sortOrder', Q.desc), Q.take(3))
      .observe() as unknown as Model<GroupDoc>[]
  }

  public GroupByID(id: string): Model<GroupDoc> {
    return (id
      ? Database.getCollection(CollectionType.GROUP).findAndObserve(id)
      : of(null)) as unknown as Model<GroupDoc>
  }

  public GroupByServerID(id: string): Model<GroupDoc> {
    return (id
      ? (Database.getCollection(CollectionType.GROUP)
          .query(Q.where('_id', id))
          .observe()
          .pipe(
            switchMap((groups) => {
              return groups[0] ? groups[0].observe() : of(null)
            })
          ) as unknown as Model<GroupDoc>)
      : of(null)) as unknown as Model<GroupDoc>
  }

  public GroupByServerIDRestricted(
    id?: string | undefined | null
  ): Model<GroupDoc> {
    return (id
      ? (Database.getCollection(CollectionType.GROUP)
          .query(Q.where('_id', id))
          .observeWithColumns(['Name'])
          .pipe(
            switchMap((groups) => {
              return groups[0] ? groups[0].observe() : of(null)
            })
          ) as unknown as Model<GroupDoc>)
      : of(null)) as unknown as Model<GroupDoc>
  }

  public Group(group: Model<GroupDoc>): Model<GroupDoc> {
    return group.observe() as unknown as Model<GroupDoc>
  }

  public FirstGroup(userSettings?: UserSettingsDoc): Model<GroupDoc> {
    const first = Database.getCollection(CollectionType.GROUP)
      .query(
        Q.sortBy('_sortOrder', Q.desc),
        Q.where('_id', Q.like(defaultGroupId(userSettings) ?? ''))
      )
      .observe()
      .pipe(
        switchMap((groups) => {
          return groups[0] ? groups[0].observe() : of(null)
        })
      ) as unknown as Model<GroupDoc>
    return first
  }

  public EventByServerID(id?: string | undefined | null): Model<EventDoc> {
    return (id
      ? (Database.getCollection(CollectionType.EVENT)
          .query(Q.where('_id', id))
          .observe()
          .pipe(
            switchMap((events) => {
              return events[0] ? events[0].observe() : of(null)
            })
          ) as unknown as Model<EventDoc>)
      : of(null)) as unknown as Model<EventDoc>
  }

  public UserSettings(): Model<UserSettingsDoc> {
    return Database.getCollection(CollectionType.USER_SETTINGS)
      .query()
      .observe()
      .pipe(
        switchMap((userSettings) =>
          userSettings[0] ? userSettings[0].observe() : of(null)
        )
      ) as unknown as Model<UserSettingsDoc>
  }

  public Events(
    type?: DocType,
    statusesToIgnore?: string[]
  ): Model<EventDoc>[] {
    const eventsQuery = []

    eventsQuery.push(Q.where('Status', Q.notIn(statusesToIgnore ?? ['3', '4'])))

    if (type) {
      eventsQuery.push(Q.where('Type', Q.like(type)))
    }

    return Database.getCollection(CollectionType.EVENT)
      .query(...eventsQuery)
      .observeWithColumns([
        'Text',
        'StartDate',
        'Status',
        'Type',
        'TaskType',
        'EndDate',
        'AllDay',
        'Color',
        'Reminder',
        'inheritedGroups',
        'LastUpdate',
        'OwnerID',
        'Priority',
        '_id',
        'Notes',
        'Recurrence',
        'ParticipantsStatus',
        'GroupID'
      ]) as unknown as Model<EventDoc>[]
  }

  public EventsForGroup(groupId: string): Model<EventDoc>[] {
    return Database.getCollection(CollectionType.EVENT)
      .query(this.EventsForGroupClause(groupId))
      .observeWithColumns([
        'Text',
        'LastUpdateMessage',
        'StartDate',
        'Status',
        'LastUpdate',
        'GroupID',
        'SupplementaryGroupsIDs',
        'inheritedGroups',
        '_rev',
        '_id'
      ]) as unknown as Model<EventDoc>[]
  }

  public ItemByID<T>(id: string, type: CollectionType): Model<T> {
    return (id
      ? Database.getCollection(type).findAndObserve(id)
      : of(null)) as unknown as Model<T>
  }

  public Profile(): Model<ProfileDoc> {
    return Database.getCollection(CollectionType.PROFILE)
      .query()
      .observe()
      .pipe(
        switchMap((profiles) =>
          profiles[0] ? profiles[0].observe() : of(null)
        )
      ) as unknown as Model<ProfileDoc>
  }

  public MasterLabel(): Model<MasterLabelDoc> {
    return Database.getCollection(CollectionType.MASTER_LABEL)
      .query()
      .observe()
      .pipe(
        switchMap((mlabel) => (mlabel[0] ? mlabel[0].observe() : of(null)))
      ) as unknown as Model<MasterLabelDoc>
  }

  public Account(): Model<AccountDoc> {
    return Database.getCollection(CollectionType.ACCOUNT)
      .query()
      .observe()
      .pipe(
        switchMap((accounts) => {
          return accounts[0] ? accounts[0].observe() : of(null)
        })
      ) as unknown as Model<AccountDoc>
  }

  public ParticipantsForIds(participantIds: string[]) {
    return ParticipantsForGroup(
      participantIds
    ).observe() as unknown as Model<ParticipantDataDoc>[]
  }

  public Participants() {
    return ParticipantsAll().observe() as unknown as Model<ParticipantDataDoc>[]
  }
}

export default new ObserveService()
